import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from '@mui/lab';
import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextFieldProps,
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';
import momentTimezone from 'moment-timezone';
import _ from 'lodash';

import {
  classDurationOptions,
  dayListOptions,
} from '../../../../../../../constants';
import {
  TUpdateCourseAsAdmin,
  getCoursesState,
  updateCourseAsAdmin,
} from '../../../../../../../store/V2/CoursesSlice';
import {
  getCurriculumV2State,
  getCurriculumsV2,
} from '../../../../../../../store/V2/CurriculumSlice';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import { IGroupTutorRateCourseType } from '../../../../../../../store/V2/GroupTutorRateCourseTypesSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/V2/RolesSlice';
import { useAppSelector } from '../../../../../../../hooks';
import { checkUserPermission } from '../../../../../../../helper';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../components/Inputs';
import GroupTutorRateCourseTypeListDropdown, {
  TGroupTutorRateCourseTypeOption,
} from '../../../../../../../components/Inputs/admin/GroupTutorRateCourseTypeListDropdown';

interface BasicDetailsTabProps {
  onClose: () => void;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  numberOfClasses: string;
  classDuration: string;
  classDays: TSelectOption[];
  classTime: Moment | null;
  courseStart: Moment | null;
  courseEnd: Moment | null;
  selectedCourseType: TGroupTutorRateCourseTypeOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const initialState: TState = {
  selectedCurriculum: null,
  selectedGrade: null,
  selectedSubject: null,
  numberOfClasses: '',
  classDuration: '60',
  classDays: [],
  classTime: null,
  courseStart: null,
  courseEnd: null,
  selectedCourseType: null,
};

const BasicDetailsTab: React.FC<BasicDetailsTabProps> = ({ onClose }) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState(initialState);
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );

  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  const [isLoading, setLoading] = useState(false);

  // Store
  const curriculumState = useAppSelector(getCurriculumV2State);
  const coursesState = useAppSelector(getCoursesState);
  const authState = useAppSelector(getAuthState);

  const courseData = coursesState.adminCourse;
  const groupTutorRateCourseTypeData =
    courseData && courseData.groupTutorRateCourseType
      ? (courseData.groupTutorRateCourseType as IGroupTutorRateCourseType)
      : undefined;

  useEffect(() => {
    dispatch(getCurriculumsV2());

    if (courseData) {
      setState((prevState) => {
        const newState: TState = { ...prevState };

        if (courseData.curriculum) {
          newState.selectedCurriculum = {
            label: courseData.curriculum,
            value: courseData.curriculum,
          };
        }

        if (courseData.grade) {
          newState.selectedGrade = {
            label: courseData.grade,
            value: courseData.grade,
          };
        }

        if (courseData.subject) {
          newState.selectedSubject = {
            label: courseData.subject,
            value: courseData.subject,
          };
        }

        if (courseData.noOfClasses) {
          newState.numberOfClasses = courseData.noOfClasses.toString();
        }

        if (courseData.duration) {
          newState.classDuration = courseData.duration.toString();
        }

        if (courseData.classDays) {
          newState.classDays = courseData.classDays.map((item) => ({
            label: item,
            value: item,
          }));
        }

        if (courseData.classTime) {
          const _utcMoment = momentTimezone.tz(
            courseData.classTime,
            'HH:mm',
            'UTC'
          );

          newState.classTime = _utcMoment.clone().tz(moment.tz.guess());
        }

        if (courseData.startDate) {
          newState.courseStart = moment(courseData.startDate);
        }

        if (courseData.endDate) {
          newState.courseEnd = moment(courseData.endDate);
        }

        if (groupTutorRateCourseTypeData) {
          newState.selectedCourseType = {
            label: groupTutorRateCourseTypeData.name,
            value: groupTutorRateCourseTypeData._id,
          };
        }

        return newState;
      });
    }
  }, []);

  useEffect(() => {
    if (curriculumState.list && curriculumState.list.length) {
      setCurriculumOptions(
        curriculumState.list.map((curriculumItem) => ({
          label: curriculumItem.curriculum,
          value: curriculumItem.curriculum,
        }))
      );

      const matchingCurriculum = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        matchingCurriculum &&
        matchingCurriculum.subjects &&
        matchingCurriculum.subjects.length
      ) {
        const newSubjectOptions: TSelectOption[] = [];

        matchingCurriculum.subjects.forEach((subjectItem) => {
          newSubjectOptions.push({
            label: subjectItem.subject,
            value: subjectItem.subject,
          });
        });

        setSubjectOptions(newSubjectOptions);

        const matchingSubject = matchingCurriculum.subjects.find(
          (subjectItem) => subjectItem.subject === state.selectedSubject?.value
        );

        if (
          matchingSubject &&
          matchingSubject.grade &&
          matchingSubject.grade.length
        ) {
          const newGradeOptions: TSelectOption[] = [];

          matchingSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });

          setGradeOptions(newGradeOptions);
        }
      }
    }
  }, [curriculumState.list]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newSubjectOptions: TSelectOption[] = [];

    if (curriculumState.list && curriculumState.list.length && value) {
      const selectedCurriculumData = curriculumState.list.find(
        (item) => item.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        selectedCurriculumData.subjects.forEach((item) => {
          newSubjectOptions.push({
            label: item.subject,
            value: item.subject,
          });
        });
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    }));

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const selectedCurriculumData = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        const selectedSubject = selectedCurriculumData.subjects.find(
          (subjectItem) => subjectItem.subject === value?.value
        );

        if (
          selectedSubject &&
          selectedSubject.grade &&
          selectedSubject.grade.length
        ) {
          selectedSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    setState((prevState) => ({
      ...prevState,
      selectedSubject: value,
      selectedGrade: null,
    }));

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedGrade: value,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState((prevState) => ({
      ...prevState,
      classDuration: event.target.value,
    }));
  };

  const handleClassDayChange = (
    event: React.SyntheticEvent,
    value: TSelectOption[]
  ) => {
    setState((prevState) => ({
      ...prevState,
      classDays: _.uniqBy(value, 'value'),
    }));
  };

  const handleClassTimeChange = (value: Moment | null) => {
    setState((prevState) => ({
      ...prevState,
      classTime: value,
    }));
  };

  const handleCourseStartDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        courseStart: value,
      }));
    }
  };

  const handleCourseEndDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        courseEnd: value,
      }));
    }
  };

  const handleSubmit = async () => {
    if (courseData) {
      setLoading(true);

      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
        classTime: '',
      };

      if (state.selectedCurriculum) {
        apiData.curriculum = state.selectedCurriculum.value;
      }

      if (state.selectedGrade) {
        apiData.grade = state.selectedGrade.value;
      }

      if (state.selectedSubject) {
        apiData.subject = state.selectedSubject.value;
      }

      if (state.numberOfClasses) {
        apiData.noOfClasses = Number(state.numberOfClasses);
      }

      if (state.classDuration) {
        apiData.duration = Number(state.classDuration);
      }

      if (state.classDays) {
        apiData.classDays = state.classDays.map((item) => item.value);
      }

      if (state.classTime) {
        apiData.classTime = state.classTime.clone().utc().format('HH:mm');
      }

      if (state.courseStart) {
        apiData.startDate = state.courseStart.clone().toISOString();
      }

      if (state.courseEnd) {
        apiData.endDate = state.courseEnd.clone().toISOString();
      }

      if (state.selectedCourseType) {
        apiData.groupTutorRateCourseType = state.selectedCourseType.value;
      } else {
        apiData.groupTutorRateCourseType = '';
      }

      if (Object.keys(apiData).length > 1) {
        await dispatch(updateCourseAsAdmin(apiData));
      }

      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '50px 30px 30px 30px' }}>
      <div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedCurriculum}
                options={curriculumOptions}
                onChange={handleCurriculumChange}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={curriculumState.status === 'loading'}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                getOptionKey={(option: TSelectOption) => option.value}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Curriculum"
                    placeholder="Curriculum"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedSubject}
                options={subjectOptions}
                onChange={handleSubjectChange}
                disabled={!state.selectedCurriculum}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={curriculumState.status === 'loading'}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                getOptionKey={(option: TSelectOption) => option.value}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Subject"
                    placeholder="Subject"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedGrade}
                options={gradeOptions}
                onChange={handleGradeChange}
                disabled={!state.selectedSubject}
                popupIcon={TPAutoCompleteArrowIconComponent}
                loading={curriculumState.status === 'loading'}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                getOptionKey={(option: TSelectOption) => option.value}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Grade"
                    placeholder="Grade"
                  />
                )}
              />
            </FormControl>
          </Grid>
          {courseData?.isOneToOne ? null : (
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  name="numberOfClasses"
                  type="number"
                  size="small"
                  label="No. of Classes"
                  placeholder="No. of Classes"
                  value={state.numberOfClasses}
                  onChange={handleInputChange}
                />
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MobileDatePicker
                  disableCloseOnSelect={false}
                  value={state.courseStart}
                  onChange={handleCourseStartDateChange}
                  label="Start Date"
                  inputFormat="YYYY MMM DD"
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Start Date"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <DateRangeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MobileDatePicker
                  disableCloseOnSelect={false}
                  value={state.courseEnd}
                  onChange={handleCourseEndDateChange}
                  label="End Date"
                  inputFormat="YYYY MMM DD"
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="End Date"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <DateRangeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                size="small"
                value={state.classDays}
                options={dayListOptions}
                onChange={handleClassDayChange}
                popupIcon={TPAutoCompleteArrowIconComponent}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    InputLabelProps={{ ...textFieldProps.InputLabelProps }}
                    sx={{
                      '& .MuiInputBase-root': {
                        minHeight: '52px',
                      },
                    }}
                    size="small"
                    label="Class Days"
                    placeholder="Select"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MobileTimePicker
                  clearable
                  value={state.classTime}
                  onChange={handleClassTimeChange}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Class Time"
                      placeholder="Class Time"
                      helperText={
                        state.classTime ? `(${deviceTimezone})` : undefined
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <AccessTimeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <FormControl fullWidth>
              <TPTextField
                select
                {...textFieldProps}
                variant="outlined"
                value={state.classDuration}
                onChange={handleDurationChange}
                label="Class Duration *"
              >
                {classDurationOptions.map((durationOption, index) => {
                  return (
                    <MenuItem key={`opt-${index}`} value={durationOption.value}>
                      {durationOption.label}
                    </MenuItem>
                  );
                })}
              </TPTextField>
            </FormControl>
          </Grid>
          {courseData && !courseData.isOneToOne ? (
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <FormControl fullWidth>
                <GroupTutorRateCourseTypeListDropdown
                  selectedItem={state.selectedCourseType}
                  componentProps={{ fullWidth: true }}
                  handleChange={(e, value) =>
                    setState((prevState) => ({
                      ...prevState,
                      selectedCourseType: value,
                    }))
                  }
                  textFieldProps={{
                    label: 'Course Type',
                    placeholder: 'Select',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
            </Grid>
          ) : null}
        </Grid>
      </div>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mt="60px"
        spacing="10px"
      >
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
        {checkUserPermission(authState.user, {
          adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
        }) ? (
          <LoadingButton
            disableElevation
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            loading={isLoading}
          >
            Save
          </LoadingButton>
        ) : null}
      </Stack>
    </div>
  );
};

export default BasicDetailsTab;
